/* eslint-disable react-hooks/exhaustive-deps */
import axios, { AxiosError, AxiosInstance } from 'axios';
import { useCallback, useEffect, useRef } from 'react';
import { useAuthHeader, useSignIn, useSignOut } from 'react-auth-kit';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from 'router/routes';
import { captureException } from '@sentry/react';
import { usePopup } from 'context/PopupContext/PopupContext';
import jwtDecode from 'jwt-decode';
import { apiRefresh } from './auth/index';

export const tzAxios: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_APP_BASE_API,
  validateStatus(status) {
    return status >= 200 && status <= 201;
  },
  headers: {
    'X-Custom-Language': 'ar',
  },
});

/** Public endpoints (e.g. B2B registration) — no auth interceptors. */
export const tzPublicAxios: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_APP_BASE_API,
  validateStatus(status) {
    return status >= 200 && status <= 201;
  },
  headers: {
    'X-Custom-Language': 'ar',
  },
});

export const useInterceptors = () => {
  const authHeader = useAuthHeader();
  const signIn = useSignIn();
  const signOut = useSignOut();
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const { showPopup } = usePopup();

  const token = authHeader();

  if (token) {
    tzAxios.defaults.headers.common.Authorization = token;
  } else {
    delete tzAxios.defaults.headers.common.Authorization;
  }

  const accessRefreshTimer = useRef<number>();
  const refreshExpiryGuardTimer = useRef<number>();
  const isRefreshing = useRef(false);

  const handleErrorCodes = useCallback(
    async (error: AxiosError<{ message: string } & Record<string, any>>) => {
      if (!error.response) {
        return Promise.reject(error);
      }

      const { status, data } = error.response;
      const message = data?.message || formatMessage({ id: 'global.error' });
      const isRefresh = isRefreshTokenRequest(error);

      const isInvitation = isInvitationRequest(error);

      switch (status) {
        case 401:
          return handle401Error(error, navigate, signIn, signOut);
        case 403:
          if (!isInvitation) {
            navigate(ROUTES.home);
            if (!isRefresh) {
              showPopup({ message, type: 'ERROR' });
            }
          }
          break;
        case 429:
          if (!isRefresh) {
            showPopup({ message, type: 'ERROR' });
          }
          break;
        default:
          if (!isRefresh && !isInvitation) {
            showPopup({ message, type: 'ERROR' });
          }
          captureException(error);
          break;
      }

      return Promise.reject(error);
    },
    [navigate, formatMessage, signIn, signOut, showPopup],
  );

  const runRefresh = useCallback(async () => {
    if (isRefreshing.current) return;
    const rt = localStorage.getItem('_auth_refresh');
    if (!rt) return;

    isRefreshing.current = true;
    try {
      const res = await apiRefresh({ refreshToken: rt });
      const nextToken = res.accessToken;
      const nextRefresh = res.refreshToken || rt;

      signIn({
        token: nextToken,
        expiresIn: res.expiresIn,
        tokenType: res.type,
        refreshToken: nextRefresh,
        refreshTokenExpireIn: 60 * 60 * 24 * 7,
      });

      tzAxios.defaults.headers.common.Authorization = `Bearer ${nextToken}`;
      scheduleAccessTokenRefresh(nextToken);
    } catch (err) {
      captureException(err);
      signOut();
      navigate(ROUTES.home, { state: { openLoginModal: true } });
    } finally {
      isRefreshing.current = false;
    }
  }, [signIn, signOut, navigate]);

  const scheduleAccessTokenRefresh = useCallback(
    (token: string) => {
      if (accessRefreshTimer.current) {
        clearTimeout(accessRefreshTimer.current);
        accessRefreshTimer.current = undefined;
      }
      try {
        const decoded: { exp?: number } = jwtDecode(token);
        if (!decoded?.exp) return;

        const now = Math.floor(Date.now() / 1000);
        const leadSeconds = 10;
        const delayMs = (decoded.exp - now - leadSeconds) * 1000;

        if (delayMs > 0) {
          accessRefreshTimer.current = window.setTimeout(runRefresh, delayMs);
        } else {
          void runRefresh();
        }
      } catch {
        /* ignore */
      }
    },
    [runRefresh],
  );

  const scheduleRefreshExpiryGuard = useCallback(() => {
    const refreshToken = localStorage.getItem('_auth_refresh');
    if (!refreshToken) return;

    try {
      const decoded: { exp?: number } = jwtDecode(refreshToken);
      if (!decoded?.exp) return;

      const now = Math.floor(Date.now() / 1000);
      const skew = 60;
      const delayMs = (decoded.exp - now - skew) * 1000;

      if (delayMs > 0) {
        refreshExpiryGuardTimer.current = window.setTimeout(() => {
          signOut();
          navigate(ROUTES.home, { state: { openLoginModal: true } });
        }, delayMs);
      }
    } catch {
      /* ignore */
    }
  }, [navigate, signOut]);

  useEffect(() => {
    tzAxios.defaults.headers.common['X-Custom-Language'] = 'ar';

    const interceptorId = tzAxios.interceptors.response.use(
      (response) => response,
      handleErrorCodes,
    );

    if (token) scheduleAccessTokenRefresh(token);
    scheduleRefreshExpiryGuard();

    return () => {
      tzAxios.interceptors.response.eject(interceptorId);
      if (accessRefreshTimer.current) clearTimeout(accessRefreshTimer.current);
      if (refreshExpiryGuardTimer.current) clearTimeout(refreshExpiryGuardTimer.current);
    };
  }, [token, handleErrorCodes, scheduleAccessTokenRefresh, scheduleRefreshExpiryGuard]);
};

const CALCULATE_PRICE_PATH = 'calculate-price';
const REFRESH_TOKEN_PATH = 'refresh-token';
const INVITATION_PATH = '/ticket-zone/invitation/';

function isCalculatePriceRequest(error: AxiosError): boolean {
  const url = error.config?.url ?? '';
  return typeof url === 'string' && url.includes(CALCULATE_PRICE_PATH);
}

function isRefreshTokenRequest(error: AxiosError): boolean {
  const url = error.config?.url ?? '';
  return typeof url === 'string' && url.includes(REFRESH_TOKEN_PATH);
}

function isInvitationRequest(error: AxiosError): boolean {
  const url = error.config?.url ?? '';
  if (typeof url === 'string' && url.includes(INVITATION_PATH)) return true;
  const params = error.config?.params as Record<string, unknown> | undefined;
  return typeof params?.invitationId === 'string' && params.invitationId.length > 0;
}

async function handle401Error(
  error: AxiosError,
  navigate: (path: string, options?: { state?: unknown }) => void,
  signIn: (props: {
    token: string;
    expiresIn: number;
    tokenType: string;
    refreshToken: string;
    refreshTokenExpireIn: number;
  }) => boolean,
  signOut: () => void,
) {
  if (isCalculatePriceRequest(error) && !localStorage.getItem('_auth_refresh')) {
    return Promise.reject(error);
  }

  if (isRefreshTokenRequest(error)) {
    signOut();
    navigate(ROUTES.home, { state: { openLoginModal: true } });
    return Promise.reject(error);
  }

  const config = error.config as { _retry?: boolean } | undefined;
  if (config?._retry) {
    signOut();
    navigate(ROUTES.home, { state: { openLoginModal: true } });
    return Promise.reject(error);
  }

  const refreshToken = localStorage.getItem('_auth_refresh');
  if (!refreshToken) {
    signOut();
    navigate(ROUTES.home);
    return Promise.reject(error);
  }

  try {
    const response = await apiRefresh({ refreshToken });
    const { accessToken, refreshToken: newRt, expiresIn, type } = response;

    signIn({
      token: accessToken,
      expiresIn: expiresIn || 60 * 15,
      tokenType: type,
      refreshToken: newRt || refreshToken,
      refreshTokenExpireIn: 60 * 60 * 24 * 7,
    });

    tzAxios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;

    if (error.config) {
      const retryConfig = {
        ...error.config,
        headers: {
          ...error.config.headers,
          Authorization: `Bearer ${accessToken}`,
        },
        _retry: true,
      };
      return tzAxios(retryConfig);
    }
  } catch (err) {
    captureException(err);
    signOut();
    navigate(ROUTES.home, { state: { openLoginModal: true } });
  }
  return Promise.reject(error);
}
